import { Badge, createDataTableColumnHelper, DataTable } from "@beweegbaas/ui";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useServerFn } from "#/modules/util.hooks/use-server-fn";
import { useState } from "react";
import { toast } from "@beweegbaas/ui";
import { DataTableActions, type DataTableAction } from "#/components/data-table-actions";
import { ErrorBoundaryComponent } from "#/components/error-boundary";
import { Page } from "#/components/page";
import {
  getDocentenOverview,
  submitRemoveDocent,
  submitSetHoofddocent,
} from "#/modules/school.detail/detail.functions";
import { CreateInviteButton } from "#/modules/school.invite/components/button.create-invite";
import { InviteMailStatusBadge } from "#/modules/school.invite/components/invite-mail-status-badge";
import {
  submitDeleteSchoolInvite,
  submitResendSchoolInvite,
} from "#/modules/school.invite/invite.functions";
import { m } from "#/paraglide/messages";
import type { components } from "#/schema";

export const Route = createFileRoute(
  "/$locale/{-$schoolyear}/_authenticated/$school/detail/docenten",
)({
  loader: () => getDocentenOverview(),
  component: DocentenPage,
  errorComponent: DocentenError,
});

type DocentModel = components["schemas"]["DocentOverviewModel"];
type InviteModel = components["schemas"]["SchooldocentInviteOverviewModel"];

// One table mixes active docenten and pending invites, so rows are a discriminated union.
type DocentenTableRow =
  | { kind: "docent"; docent: DocentModel }
  | { kind: "invite"; invite: InviteModel };

const formatDateTime = (dateString: string) =>
  new Date(dateString).toLocaleString("nl-NL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

function DocentActions({ docent }: { docent: DocentModel }) {
  const router = useRouter();
  const submitSetHoofdDocentFn = useServerFn(submitSetHoofddocent);
  const submitRemoveDocentFn = useServerFn(submitRemoveDocent);

  const hasActions = docent.canBeMadeHoofdDocent || docent.canBeRemoved;
  if (!hasActions) return null;

  const actions: DataTableAction[] = [
    ...(docent.canBeMadeHoofdDocent
      ? [
          {
            key: "make-hoofddocent",
            label: m["school.docenten.actions.make_hoofddocent"](),
            icon: <IconMaterialSymbolsPersonAddOutlineRounded />,
            confirm: {
              title: m["school.docenten.hoofddocent.confirm_title"](),
              message: m["school.docenten.hoofddocent.confirm_message"]({
                docentName: docent.name,
              }),
            },
            onClick: async () => {
              await submitSetHoofdDocentFn({ data: { schoolDocentId: docent.schooldocentId } });
              toast.success(m["school.docenten.success.hoofddocent_updated"]());
              await router.invalidate();
            },
          },
        ]
      : []),
    ...(docent.canBeRemoved
      ? [
          {
            key: "remove",
            label: m["common.actions.unlink"](),
            icon: <IconMaterialSymbolsDelete />,
            destructive: true,
            confirm: {
              title: m["common.confirm.unlink_docent.title"](),
              message: m["common.confirm.unlink_docent.message"]({ docentName: docent.name }),
              confirmLabel: m["common.actions.unlink"](),
            },
            onClick: async () => {
              await submitRemoveDocentFn({ data: { schooldocentId: docent.schooldocentId } });
              toast.success(m["school.docenten.success.docent_removed"]());
              await router.invalidate();
            },
          },
        ]
      : []),
  ];

  return <DataTableActions actions={actions} />;
}

function InviteActions({ invite }: { invite: InviteModel }) {
  const router = useRouter();
  const resendInvite = useServerFn(submitResendSchoolInvite);
  const deleteInvite = useServerFn(submitDeleteSchoolInvite);
  const [isPending, setIsPending] = useState(false);

  const handleResend = async () => {
    setIsPending(true);
    try {
      await resendInvite({ data: { inviteId: invite.id } });
    } catch {
      // toastErrorMiddleware already surfaced the error
      return;
    } finally {
      setIsPending(false);
    }
    toast.success(m["school.docenten.success.invite_resent"]());
    await router.invalidate();
  };

  if (!invite.canResend && !invite.canDelete) return null;

  const actions: DataTableAction[] = [
    ...(invite.canResend
      ? [
          {
            key: "resend",
            label: m["school.docenten.actions.resend"](),
            icon: <IconMaterialSymbolsRefresh />,
            isPending,
            onClick: () => void handleResend(),
          },
        ]
      : []),
    ...(invite.canDelete
      ? [
          {
            key: "delete",
            label: m["school.docenten.invite.delete.confirm_title"](),
            icon: <IconMaterialSymbolsDelete />,
            destructive: true,
            isPending,
            confirm: {
              title: m["school.docenten.invite.delete.confirm_title"](),
              message: m["school.docenten.invite.delete.confirm_message"]({
                email: invite.inviteeEmail,
              }),
              confirmLabel: m["school.docenten.invite.delete.confirm_title"](),
            },
            onClick: async () => {
              await deleteInvite({ data: { inviteId: invite.id } });
              toast.success(m["school.docenten.uitnodiging_verwijderd_toast"]());
              await router.invalidate();
            },
          },
        ]
      : []),
  ];

  return <DataTableActions actions={actions} />;
}

const helper = createDataTableColumnHelper<DocentenTableRow>();

const columns = helper.columns([
  helper.display({
    id: "name",
    header: () => m["common.fields.name"](),
    cell: ({ row }) =>
      row.original.kind === "docent" ? (
        <div className="flex items-center gap-2">
          {row.original.docent.name}
          {row.original.docent.isHoofdDocent && (
            <Badge variant="blue" size="sm">
              {m["common.labels.hoofddocent"]()}
            </Badge>
          )}
        </div>
      ) : (
        "-"
      ),
  }),
  helper.display({
    id: "email",
    header: () => m["common.fields.email"](),
    cell: ({ row }) =>
      row.original.kind === "docent" ? row.original.docent.email : row.original.invite.inviteeEmail,
  }),
  helper.display({
    id: "status",
    header: () => m["common.fields.status"](),
    cell: ({ row }) =>
      row.original.kind === "docent" ? (
        <Badge variant="green" size="sm">
          {m["common.status.actief"]()}
        </Badge>
      ) : (
        <div className="flex flex-wrap items-center gap-1">
          <Badge variant="gray" size="sm">
            {m["school.docenten.status.pending"]()}
          </Badge>
          <InviteMailStatusBadge delivery={row.original.invite.mailDelivery} />
        </div>
      ),
  }),
  helper.display({
    id: "lastActivity",
    header: () => m["school.docenten.table.last_activity"](),
    cell: ({ row }) =>
      row.original.kind === "docent"
        ? row.original.docent.lastActivityAt
          ? formatDateTime(row.original.docent.lastActivityAt)
          : m["common.labels.never_logged_in"]()
        : m["school.docenten.invite.expires_at"]({
            date: formatDateTime(row.original.invite.expiresAt),
          }),
  }),
  helper.display({
    id: "actions",
    header: "",
    meta: { action: true, headerClassName: "w-0" },
    cell: ({ row }) =>
      row.original.kind === "docent" ? (
        <DocentActions docent={row.original.docent} />
      ) : (
        <InviteActions invite={row.original.invite} />
      ),
  }),
]);

function DocentenPage() {
  const { school } = Route.useParams();
  const { docenten, invites, canAddDocent, currentDocenten, maxDocenten } = Route.useLoaderData();
  const pendingInvites = invites.filter((invite) => invite.status === "Pending");

  const rows: DocentenTableRow[] = [
    ...docenten.map((docent) => ({ kind: "docent" as const, docent })),
    ...pendingInvites.map((invite) => ({ kind: "invite" as const, invite })),
  ];

  return (
    <>
      {!canAddDocent && <Page.MobileHeader />}
      {canAddDocent && (
        <Page.Header>
          <Page.Title>
            Docenten{" "}
            <span className="text-muted-foreground text-base font-normal">
              ({currentDocenten}/{maxDocenten})
            </span>
          </Page.Title>
          <Page.Header.Actions>
            <CreateInviteButton
              school={school}
              limitReached={currentDocenten >= maxDocenten}
              maxDocenten={maxDocenten}
              className="max-sm:w-full"
              size="xl"
            >
              <IconMaterialSymbolsAdd />
              {m["school.docenten.actions.invite"]()}
            </CreateInviteButton>
          </Page.Header.Actions>
        </Page.Header>
      )}
      <Page.Body className="min-h-0">
        <DataTable
          alignToContainer
          aria-label={m["common.labels.docenten"]()}
          columns={columns}
          data={rows}
          getRowId={(row) => (row.kind === "docent" ? row.docent.schooldocentId : row.invite.id)}
          empty={m["common.empty_state.no_docenten"]()}
        />
      </Page.Body>
    </>
  );
}

function DocentenError({ error }: { error: unknown }) {
  const params = Route.useParams();
  return <ErrorBoundaryComponent error={error} params={params} />;
}
